import React, {useEffect, useState} from 'react';
import {Navigation} from '@/components/Navigation';
import {Footer} from '@/components/Footer';
import {
    MessageSquare,
    Users,
    Plus,
    ChevronRight,
    ChevronLeft,
    Flag,
    Trophy,
    Settings,
    Award,
    Search,
    Filter,
    Check,
    X,
    UserPlus,
    Send,
    Lock,
    Unlock,
    AlertTriangle, Construction
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import {Button} from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Card, CardHeader, CardContent} from '@/components/ui/card';
import {Table, TableHeader, TableRow, TableHead, TableBody, TableCell} from '@/components/ui/table';
import {Textarea} from '@/components/ui/textarea';
import {Checkbox} from '@/components/ui/checkbox';
import {ScrollArea} from '@/components/ui/scroll-area';
import {Head} from '@/components/Head';
import {useIsMobile} from '@/hooks/use-mobile';
import usernames from '@/data/usernames.json';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs';
import {Select, SelectTrigger, SelectValue, SelectContent, SelectItem} from '@/components/ui/select';
import {useTheme} from '@/components/theme-provider';
import {Alert, AlertDescription} from "@/components/ui/alert";
import {useQuery} from "@tanstack/react-query";
import {LucideProps} from "lucide-react";
import {Session} from "@supabase/supabase-js";
import {Category} from "@/types/Category.ts";
import {Clan} from "@/types/Clan.ts";
import {SEO} from "@/components/SEO.tsx";
import {toast} from "sonner";
import ClanDetailModal from "@/components/ClanDetailModal.tsx";
import ClanManagementModal from "@/components/ClanManagementModal.tsx";
import {User} from "@/types/User.ts";
import {ClanApplication} from "@/types/ClanApplication.ts";
import {ClanMember} from "@/types/ClanMember.ts";
import supabaseClient from "@/utils/supabaseClient.ts";
import {useNavigate} from "react-router-dom";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import ClanCardSkeleton from "@/components/ClanCardSkeleton.tsx";
import LeagueCardSkeleton from "@/components/LeagueCardSkeleton.tsx";
import {useAuth} from "@/contexts/AuthContext.tsx";

type IconComponent = React.FC<LucideProps>;

function isIconComponent(icon): icon is IconComponent {
    return typeof icon === "function" || typeof icon === "object";
}

const CHANNELS = [
    {id: '1', name: 'General', users: 142, isPrivate: false},
    {id: '2', name: 'Strategy', users: 76, isPrivate: true},
    {id: '3', name: 'Off-Topic', users: 52, isPrivate: false},
];

const generateClanUser = (index: number) => {
    const name = usernames.usernames[Math.floor(Math.random() * usernames.usernames.length)];
    const bp = Math.floor(Math.random() * 900000) + 100000;
    const correct = Math.floor(Math.random() * 500) + 100;
    const badges = ['🔥', '⭐', '👑', '🌟'].filter(() => Math.random() > 0.5);
    return {id: index.toString(), name, bp, correct, badges};
};

const CLANS = [
    {
        id: '1',
        name: 'Pollsters United',
        bp: 5750000,
        users: Array.from({length: 12}, (_, i) => generateClanUser(i + 1)),
        category: ['Finance', 'Technology']
    },
    {
        id: '2',
        name: 'Prediction Masters',
        bp: 4230000,
        users: Array.from({length: 8}, (_, i) => generateClanUser(i + 20)),
        category: ['Fun', 'Sports']
    },
    {
        id: '3',
        name: 'Fortune Tellers',
        bp: 3980000,
        users: Array.from({length: 15}, (_, i) => generateClanUser(i + 40)),
        category: ['Politics', 'Economics']
    },
];

const CLAN_TIERS = [
    {
        id: '1',
        name: 'Champions League',
        criteria: 'Top 10 users after first 3 (min 1M bp)',
        benefits: 'Poll points +3.2% bp, 7000 bp joining bonus',
        color: '#E7F5FD',
        textColor: '#1A385F',
        icon: Trophy
    },
    {
        id: '2',
        name: 'Amateur League',
        criteria: 'All users below 250K bp',
        benefits: 'Standard bp',
        color: '#FFF7DF',
        textColor: '#6F3B01',
        icon: Flag
    },
    {
        id: '3',
        name: 'Chaos League',
        criteria: 'Users with 500K+ bp',
        benefits: 'Poll points +2% bp, 3000 bp joining bonus',
        color: '#E5F5EA',
        textColor: '#155231',
        icon: Award
    },
    {
        id: '4',
        name: 'Legends League',
        criteria: 'Top 3 users for at least 6 months',
        benefits: 'Poll points +5% bp, 20000 bp joining bonus',
        color: '#E7F5FD',
        textColor: '#1A385F',
        icon: Trophy
    },
    {
        id: '5',
        name: 'Rising Stars League',
        criteria: 'Users with 100K-250K bp',
        benefits: 'Poll points +1.5% bp, 1500 bp joining bonus',
        color: '#FFF7DF',
        textColor: '#6F3B01',
        icon: Award
    },
    {
        id: '6',
        name: 'Professionals League',
        criteria: 'Users with 250K-500K bp',
        benefits: 'Poll points +2.5% bp, 5000 bp joining bonus',
        color: '#E5F5EA',
        textColor: '#155231',
        icon: Flag
    }
];

const CATEGORIES = [
    'Politics', 'Fun', 'Sports', 'Technology',
    'Science', 'Health', 'Finance', 'Economics', 'Education', 'Environment'
];

const generateMessage = (channelId: string) => {
    // const username = usernames.usernames[Math.floor(Math.random() * usernames.usernames.length)];
    const username = "blankpoll admin"
    /*const texts = [
        "Just voted on that new poll!",
        "Anyone know when the next big poll is coming?",
        "I think I'm going to hit 1M bp soon!",
        "Has anyone seen the leaderboard today?",
        "What's your prediction strategy?",
        "How does everyone feel about the new poll format?",
        "I just earned a new badge!",
        "Anyone want to join our clan?",
        "The last poll was super challenging",
        "I'm on a winning streak right now"
    ];*/
    const texts = [
        "Live chat will be coming soon!"
    ]
    const text = texts[Math.floor(Math.random() * texts.length)];
    return {
        id: Math.random().toString(),
        channelId,
        username,
        text,
        timestamp: new Date(Date.now() - Math.floor(Math.random() * 3600000)).toISOString()
    };
};

const MESSAGES = CHANNELS.flatMap(channel =>
    // Array.from({length: 15}, () => generateMessage(channel.id))
    Array.from({length: 1}, () => generateMessage(channel.id))
);

const generateClanRequest = (index: number) => {
    const name = usernames.usernames[Math.floor(Math.random() * usernames.usernames.length)];
    const bp = Math.floor(Math.random() * 900000) + 100000;
    const correct = Math.floor(Math.random() * 500) + 100;
    const badges = ['🔥', '⭐', '👑', '🌟'].filter(() => Math.random() > 0.5);
    return {id: index.toString(), name, bp, correct, badges, status: 'pending'};
};

const CLAN_REQUESTS = Array.from({length: 8}, (_, i) => generateClanRequest(i + 100));

interface ClanCreate {
    name: string,
    description: string,
    categories: string[],
    joinCriteria: { type: string, value: number },
    privacy: 'public' | 'private',
    createdBy: string,
    icon?: string,
}

const Clans: React.FC = () => {
    const [activeChannel, setActiveChannel] = useState(CHANNELS[0].id);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [channelSearchTerm, setChannelSearchTerm] = useState('');
    const isMobile = useIsMobile();
    const [createClanOpen, setCreateClanOpen] = useState(false);
    const [manageClanOpen, setManageClanOpen] = useState(false);
    const [createChannelOpen, setCreateChannelOpen] = useState(false);
    const [membershipRequestsOpen, setMembershipRequestsOpen] = useState(false);
    const [newChannelName, setNewChannelName] = useState('');
    const [selectedCategoryFilters, setSelectedCategoryFilters] = useState<string[]>([]);

    const [clanName, setClanName] = useState('');
    const [clanDescription, setClanDescription] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
    const [maxMembers, setMaxMembers] = useState<string>('');
    const [minBP, setMinBP] = useState<string>('');
    const [unlimited, setUnlimited] = useState(false);
    const [privacy, setPrivacy] = useState<string>('public');
    const [channelPrivacy, setChannelPrivacy] = useState<string>('public');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const {theme} = useTheme();
    /*const [userSession] = useState<Session | null>(() => {
        const cachedSession = localStorage.getItem('session');
        return cachedSession ? JSON.parse(cachedSession) : null;
    });*/
    const {user, session, loading} = useAuth();

    const [filteredClans, setFilteredClans] = useState<Clan[]>();
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const [userClanId, setUserClanId] = useState<string | null>(null);
    const [userCanCreateClan, setUserCanCreateClan] = useState<boolean>(false);
    const [userCanCreateClanError, setUserCanCreateClanError] = useState<string>("");
    const navigate = useNavigate();
    const [canApply, setCanApply] = useState<boolean>(true);
    const [userMembership, setUserMembership] = useState(null);
    const {data: clanMembership, refetch: clanMembershipRefetch, isLoading: isClanMembershipLoading} = useQuery({
        queryKey: ['clanMembership',session],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/users/clan`, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${session.access_token}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();

            if (responseData?.data) {
                setUserClanId(responseData.data.clanId)
                setCanApply(false)
            }
            if (responseData?.data.role == 'owner') {
                setIsOwner(true);
                setCanApply(false)
            }
            // console.log("category response:", responseData)
            return responseData;
        },
        retry: 1,
        retryDelay: 1000
    });
    useEffect(() => {
        setUserMembership(clanMembership);
    }, [clanMembership])

    const {data: canCreateClan, refetch: canCreateClanRefetch, isLoading: isCanCreateClanLoading} = useQuery({
        queryKey: ['canCreateClan',session],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/check`, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${session.access_token}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();

            // console.log("canCreateClan:", responseData);
            if (responseData?.success) {
                setUserCanCreateClan(true);
            } else {
                setUserCanCreateClan(false);
                setUserCanCreateClanError(responseData.error);
            }
            // console.log("category response:", responseData)
            return responseData;
        },
        retry: 5,
        retryDelay: 1000
    });

    const {data: clanApplications, refetch: clanApplicationsRefetch, isLoading: isClanApplicationsLoading} = useQuery({
        queryKey: ['clanApplications',session],
        queryFn: async () => {
            /* const refreshToken = userSession.refresh_token;
             const {data: session, error} = await supabaseClient.auth.refreshSession({refresh_token: refreshToken})
 */
            const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${userClanId}/manage/applications`, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${session.access_token}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();
            const returnData: ClanApplication[] = responseData?.data || null;
            // console.log("applications:", returnData);
            // console.log("category response:", responseData)
            return returnData;
        },
        enabled: isOwner,
        retry: false
    });

    const {data: clanMembers, refetch: clanMembersRefetch, isLoading: isClanMembersLoading} = useQuery({
        queryKey: ['clanMembers',session],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${userClanId}/manage/members`, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${session.access_token}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();
            const returnData: ClanMember[] = responseData?.data || null;
            // console.log("category response:", responseData)
            return returnData;
        },
        enabled: isOwner,
        retry: false
    });

    const {data: categories, isLoading: isCategoriesLoading} = useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/categories`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData: Category[] = await response.json();
            // console.log("category response:", responseData)
            return responseData;
        }
    });

    const {data: leagues, isLoading: isLeaguesLoading} = useQuery({
        queryKey: ['leagues'],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/leagues`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();
            // console.log("category response:", responseData)
            return responseData;
        }
    });

    const {data: clans, isLoading: isClansLoading, refetch: clansRefetch} = useQuery({
        queryKey: ['clans'],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/clans`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();
            const mapped: Clan[] = responseData
            // console.log("category response:", responseData)
            return mapped;
        }
    });

    useEffect(() => {
        // console.log("this")
        if (clans) {
            // console.log("clans:",clans);
            setFilteredClans(clans.filter(clan => {
                const matchesSearchTerm = clan.name.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesCategories = selectedCategoryFilters.length === 0 ||
                    selectedCategoryFilters.some(category => clan.categoryNames.includes(category));
                return matchesSearchTerm && matchesCategories;
            }))
        }
    }, [clans])

    useEffect(() => {
        // console.log("selectedCategoryFilters:",selectedCategoryFilters)
        if (clans) {
            setFilteredClans(clans.filter(clan => {
                let matchesSearchTerm = true;
                if (searchTerm.trim() != "") {
                    matchesSearchTerm = clan.name.toLowerCase().includes(searchTerm.toLowerCase());
                }

                const matchesCategories = selectedCategoryFilters.length === 0 ||
                    selectedCategoryFilters.some(category => clan.categories.includes(category));
                return matchesSearchTerm && matchesCategories;
            }))
        }
    }, [searchTerm, selectedCategoryFilters])

    const channelMessages = MESSAGES.filter(msg => {
        const channel = CHANNELS.find(c => c.id === msg.channelId);
        if (channel && channel.isPrivate) {
            return false;
        }
        return msg.channelId === activeChannel;
    }).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    const filteredChannels = CHANNELS.filter(channel =>
        channel.name.toLowerCase().includes(channelSearchTerm.toLowerCase())
    );


    const [isApplied, setIsApplied] = useState(true);
    const [clanDetailsOpen, setClanDetailsOpen] = useState(false);
    const [clanDetail, setClanDetail] = useState<Clan>(null);
    const [clanDetailLoading, setClanDetailLoading] = useState(false);
    const showClanDetails = async (clanId: string) => {
        setClanDetailLoading(true);
        const clan = await findClanById(clanId);
        // console.log("clan:", clanId);
        // console.log("clanById:", clanById);

        // setClanDetailsOpen(true);
        setClanDetail(clan);
        setClanDetailLoading(false);
        setClanDetailsOpen(true)
    };
    const userClanApplication = async (clanId) => {
        if (session?.access_token) {
            const clanApplication = await findClanApplication(clanId)
            setIsApplied(clanApplication.success);
        }
    }

    const findClanApplication = async (clanId: string) => {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${clanId}/apply`, {
            method: 'GET',
            headers: {
                "Authorization": `Bearer ${session.access_token}`
            }
        });
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const responseData = await response.json();
        // console.log("category response:", responseData)
        return responseData;
    }

    const findClanById = async (clanId: string) => {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${clanId}`);
        const responseData = await response.json();
        return responseData;
    }

    const applyToClan = async (clanId: string) => {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${clanId}/apply`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`
            }
        });

        const responseData = await response.json();
        if (responseData.error) {
            toast.error(responseData.error);
        } else {
            toast.success("Your application has been sent. Wait for approval.");
            setIsApplied(true);
        }
    }

    const onApplyClan = async (clanId: string) => {
        await applyToClan(clanId);
    }

    const onLeaveClan = async (clanId: string) => {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${userClanId}/leave`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`
            }
        });
        const responseData = await response.json();
        // console.log("manage application:", responseData);
        if (responseData.success) {
            toast.success("You left the clan!");
            setClanDetailsOpen(false);
        } else {
            toast.error(responseData.error || "An error occurred!");
        }
        setUserClanId("");
        await clansRefetch();
        await clanMembershipRefetch();
    }

    const manageClanApplications = async (applicantId: string, action: string) => {
        // console.log(applicantId);
        const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${userClanId}/manage/applications/${action}`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify({applicantId: applicantId}),
        });
        const responseData = await response.json();
        // console.log("manage application:", responseData);
        if (responseData.success) {
            if (action == "approve") {
                toast.success("You have new member!")
            } else {
                toast.success("Rejected!");
            }
        } else {
            toast.error(responseData.error || "An error occurred!");
        }
        await clanApplicationsRefetch();
        await clanMembersRefetch();
        await clansRefetch();
    }

    const manageClanMembers = async (memberId: string) => {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/clans/${userClanId}/manage/members/kick`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify({memberId: memberId}),
        });
        const responseData = await response.json();
        if (responseData.success) {
            toast.success("Member Kicked!")
        } else {
            toast.error(responseData.error || "An error occurred!");
        }
        await clanApplicationsRefetch();
        await clanMembersRefetch();
    }

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [minPoints, setMinPoints] = useState<string>("");
    const [description, setDescription] = useState<string>("");



    const handleKickMember = async (memberId: string) => {
        await manageClanMembers(memberId);
    }

    const handleApproveRequest = async (applicantId: string) => {
        await manageClanApplications(applicantId, 'approve')
    };

    const handleRejectRequest = async (applicantId: string) => {
        await manageClanApplications(applicantId, 'reject')
    };

    const handleSendMessage = (e: React.FormEvent) => {
        e.preventDefault();
        if (message.trim()) {
            // console.log('Sending message:', message);
            setMessage('');
        }
    };

    const handleCategoryToggle = (category: Category) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    };

    const handleCategoryFilterToggle = (category: string) => {
        setSelectedCategoryFilters(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    };

    const handleUserToggle = (username: string) => {
        setSelectedUsers(prev =>
            prev.includes(username)
                ? prev.filter(u => u !== username)
                : [...prev, username]
        );
    };

    const callCreateClan = async () => {
        if (clanName == '' || clanDescription == '') {
            return false;
        }
        const clanCreate: ClanCreate = {
            name: clanName,
            description: clanDescription,
            categories: selectedCategories.map(category => {
                return category.id
            }),
            joinCriteria: {type: "points", value: parseInt(minBP) || 0},
            privacy: privacy as 'public' | 'private',
            createdBy: session.user?.id || "",
        }
        const url = `${import.meta.env.VITE_API_URL}/clans`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify(clanCreate)
        });
        const responseData = await response.json();
        // console.log("create response:", responseData);
        if (responseData.error) {
            toast.error(responseData.error);
            return false;
        } else {
            clanMembershipRefetch();
            toast.error("You are the Clan Master now!");
            return true;
        }
    }

    const checkCreateClan = async () => {
        // console.log("userCanCreateClan:", userCanCreateClan);
        // console.log("userCanCreateClanError:", userCanCreateClanError);
        if (!userCanCreateClan) {
            toast.error(userCanCreateClanError);
            return;
        } else {
            setClanName('');
            setSelectedCategories([]);
            setMaxMembers('');
            setMinBP('');
            setUnlimited(false);
            setCreateClanOpen(true)
        }
    }

    const handleCreateClan = async (e: React.FormEvent) => {
        e.preventDefault();

        const response = await callCreateClan();
        if (response) {
            setCreateClanOpen(false);
            setClanName('');
            setSelectedCategories([]);
            setMaxMembers('');
            setMinBP('');
            setUnlimited(false);
        }

    };
    useEffect(() => {
        if (!clanDetailsOpen) {
            (document.activeElement as HTMLElement)?.blur();
        }
    }, [clanDetailsOpen]);

    const handleCreateChannel = (e: React.FormEvent) => {
        e.preventDefault();
        if (newChannelName.trim()) {
            /*console.log('Creating channel:', {
                name: newChannelName,
                privacy: channelPrivacy,
                allowedUsers: selectedUsers
            });*/
            setCreateChannelOpen(false);
            setNewChannelName('');
            setChannelPrivacy('public');
            setSelectedUsers([]);
        }
    };

    const formatNumber = (num: number) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const selectedChannel = CHANNELS.find(c => c.id === activeChannel);
    const isChannelPrivate = selectedChannel?.isPrivate || false;

    return (
        <>
            <SEO
                title="Clans"
                description="Join a clan, compete with others, and rise through the leagues"
                type="website"
                name="blankpoll"
                imageUrl="/blankpoll-banner.jpg"
            />
            <Navigation/>

            <main className="pt-20 pb-12 min-h-screen bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row gap-6">

                        {/*<div className="w-full md:w-1/3 lg:w-1/4 md:sticky md:top-24 md:self-start hidden md:block">
                            <Card className="h-[calc(100vh-140px)]">
                                <CardHeader className="p-4 border-b">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-lg font-semibold flex items-center">
                                            <MessageSquare className="h-5 w-5 mr-2"/>
                                            Live Chat
                                        </h2>
                                    </div>
                                </CardHeader>
                                <div className="flex flex-col h-[calc(100%-64px)]">
                                    <div className="p-2 border-b">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-sm">Channels</span>

                                            <Dialog open={createChannelOpen} onOpenChange={setCreateChannelOpen}>
                                                <DialogTrigger asChild>
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled>
                                                        <Plus className="h-4 w-4"/>
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="max-w-md">
                                                    <DialogHeader>
                                                        <DialogTitle>Create New Channel</DialogTitle>
                                                        <DialogDescription>
                                                            Create a new channel for clan members to chat in
                                                        </DialogDescription>
                                                    </DialogHeader>
                                                    <form onSubmit={handleCreateChannel} className="space-y-4 mt-2">
                                                    <form className="space-y-4 mt-2">
                                                        <div className="space-y-2">
                                                            <Label htmlFor="channelName">Channel Name</Label>
                                                            <Input
                                                                id="channelName"
                                                                value={newChannelName}
                                                                onChange={(e) => setNewChannelName(e.target.value)}
                                                                placeholder="Enter channel name"
                                                                required
                                                            />
                                                        </div>

                                                        <div className="space-y-2">
                                                            <Label htmlFor="channelPrivacy">Privacy</Label>
                                                            <Select value={channelPrivacy}
                                                                    onValueChange={setChannelPrivacy}>
                                                                <SelectTrigger id="channelPrivacy"
                                                                               className="bg-white dark:bg-[#1F2937]">
                                                                    <SelectValue placeholder="Select privacy"/>
                                                                </SelectTrigger>
                                                                <SelectContent className="bg-white dark:bg-[#1F2937]">
                                                                    <SelectItem value="public"
                                                                                className="flex items-center">
                                                                        <div className="flex items-center">
                                                                            <Unlock className="h-4 w-4 mr-2"/>
                                                                            <span>Public</span>
                                                                        </div>
                                                                    </SelectItem>
                                                                    <SelectItem value="private"
                                                                                className="flex items-center">
                                                                        <div className="flex items-center">
                                                                            <Lock className="h-4 w-4 mr-2"/>
                                                                            <span>Private</span>
                                                                        </div>
                                                                    </SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {channelPrivacy === 'private' && (
                                                            <div className="space-y-2">
                                                                <Label>Select Users</Label>
                                                                <div
                                                                    className="border rounded-md p-2 max-h-40 overflow-y-auto bg-white dark:bg-[#1F2937]">
                                                                    <div className="mb-2">
                                                                        <Input
                                                                            placeholder="Search users..."
                                                                            className="mb-2"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        {usernames.usernames.slice(0, 10).map((username) => (
                                                                            <div key={username}
                                                                                 className="flex items-center space-x-2">
                                                                                <Checkbox
                                                                                    id={`user-${username}`}
                                                                                    checked={selectedUsers.includes(username)}
                                                                                    onCheckedChange={() => handleUserToggle(username)}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`user-${username}`}
                                                                                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                                                >
                                                                                    @{username}
                                                                                </label>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                {selectedUsers.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                                        {selectedUsers.map(user => (
                                                                            <div
                                                                                key={user}
                                                                                className="flex items-center gap-1 bg-primary/10 dark:bg-gray-700 rounded-full px-2 py-1 text-xs"
                                                                            >
                                                                                <span>@{user}</span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleUserToggle(user)}
                                                                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                                                >
                                                                                    <X className="h-3 w-3"/>
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        <DialogFooter>
                                                            <Button type="submit" disabled>Create Channel</Button>
                                                        </DialogFooter>
                                                    </form>
                                                </DialogContent>
                                            </Dialog>
                                        </div>

                                        <div className="relative mb-2">
                                            <Search
                                                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4"/>
                                            <Input
                                                placeholder="Search channels..."
                                                value={channelSearchTerm}
                                                onChange={(e) => setChannelSearchTerm(e.target.value)}
                                                className="pl-8 w-full"
                                                disabled
                                            />
                                        </div>

                                        <ScrollArea className="h-36">
                                            <div className="flex flex-col space-y-1">
                                                {filteredChannels.map((channel) => (
                                                    <Button
                                                        key={channel.id}
                                                        variant={activeChannel === channel.id ? "default" : "ghost"}
                                                        size="sm"
                                                        className="justify-start w-full dark:hover:text-[#09090B]"
                                                        onClick={() => setActiveChannel(channel.id)}
                                                    >
                                                        #{channel.name}
                                                        <span
                                                            className="ml-1 text-xs opacity-70">+{channel.users}</span>
                                                        {channel.isPrivate && (
                                                            <span className="ml-auto">
                                <Lock className="h-3.5 w-3.5 opacity-80"/>
                              </span>
                                                        )}
                                                    </Button>
                                                ))}
                                            </div>
                                        </ScrollArea>
                                    </div>

                                    <ScrollArea className="flex-1 p-3">
                                        {isChannelPrivate ? (
                                            <Alert variant="warning"
                                                   className="bg-yellow-100 dark:bg-yellow-950 border-yellow-300 dark:border-yellow-800">
                                                <AlertTriangle
                                                    className="h-4 w-4 text-yellow-600 dark:text-yellow-500"/>
                                                <AlertDescription className="text-yellow-800 dark:text-yellow-400">
                                                    This channel is private. You don't have access to view messages.
                                                </AlertDescription>
                                            </Alert>
                                        ) : (
                                            <div className="space-y-3">
                                                {channelMessages.map((msg) => (
                                                    <div key={msg.id} className="flex items-start gap-2">
                                                        <div
                                                            className="bg-primary/10 rounded-full p-2 text-xs font-medium">
                                                            {msg.username.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-baseline gap-2">
                                                                <span
                                                                    className="font-medium text-sm">@{msg.username}</span>
                                                                <span className="text-xs text-muted-foreground">
                                  {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                </span>
                                                            </div>
                                                            <p className="text-sm">{msg.text}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </ScrollArea>

                                    <form onSubmit={handleSendMessage} className="p-3 border-t mt-auto">
                                        <div className="flex gap-2">
                                            <Input
                                                placeholder="Type a message..."
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                className="flex-1"
                                                // disabled={isChannelPrivate}
                                                disabled
                                            />
                                            <Button type="submit" size="sm" disabled={isChannelPrivate}>Send</Button>
                                            <Button type="submit" size="sm" disabled>Send</Button>
                                        </div>
                                    </form>
                                </div>
                            </Card>
                        </div>*/}

                        <div className="w-full sm:full md:w-full lg:w-full space-y-6">
                            <div
                                className="flex flex-row justify-between items-start sm:items-start  gap-4">
                                <h1 className="text-3xl font-bold">Clans</h1>

                                <div className="flex space-x-3">

                                    {(userMembership?.data && userMembership?.data.role == 'owner' && !isClanMembershipLoading) && (
                                        <ClanManagementModal
                                            manageClanOpen={manageClanOpen}
                                            setManageClanOpen={setManageClanOpen}
                                            clanApplications={clanApplications}
                                            handleApproveRequest={handleApproveRequest}
                                            handleRejectRequest={handleRejectRequest}
                                            handleKickMember={handleKickMember}
                                            members={clanMembers}
                                            membersRefetch={clanMembersRefetch}
                                            applicationsRefetch={clanApplicationsRefetch}
                                            // handleSaveSettings={handleSaveSettings}
                                            isSaving={isSaving}
                                            setIsSaving={setIsSaving}
                                            minPoints={minPoints}
                                            setMinPoints={setMinPoints}
                                            description={description}
                                            setDescription={setDescription}
                                            clanId={userClanId}
                                        />
                                    )}

                                    {(userMembership?.data && userMembership?.data.role == 'member' && !isClanMembershipLoading) && (
                                        <ClanDetailModal
                                            clanId={userClanId} clanDetails={clanDetail}
                                            open={clanDetailsOpen}
                                            setOpen={setClanDetailsOpen} onApply={onApplyClan}
                                            isApplied={isApplied} userSession={session}
                                            userClanApplication={userClanApplication}
                                            showClanDetails={showClanDetails}
                                            clanDetailLoading={clanDetailLoading}
                                            onLeave={onLeaveClan}
                                            canApply={false}
                                        />
                                    )}

                                    {(!userMembership?.error && !userMembership?.data && !isClanMembershipLoading) && (
                                        <Dialog open={createClanOpen} onOpenChange={setCreateClanOpen}>
                                            <DialogTrigger asChild onClick={(e) => {
                                                e.preventDefault();
                                                if (!session?.access_token) {
                                                    toast.error("You must log in to create clan!")
                                                } else {
                                                    checkCreateClan()
                                                }
                                            }}>
                                                <Button className="whitespace-nowrap">
                                                    <Plus className="h-4 w-4 mr-1"/>
                                                    Create Clan
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-md">
                                                <DialogHeader>
                                                    <DialogTitle>Create New Clan</DialogTitle>
                                                </DialogHeader>
                                                <form onSubmit={handleCreateClan} className="space-y-4 mt-2">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="clanName">Clan Name</Label>
                                                        <Input
                                                            id="clanName"
                                                            value={clanName}
                                                            onChange={(e) => setClanName(e.target.value)}
                                                            placeholder="Enter clan name"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor={"clanDescription"}>Description</Label>
                                                        <Input
                                                            id="clanDescription"
                                                            value={clanDescription}
                                                            onChange={(e) => setClanDescription(e.target.value)}
                                                            placeholder="Enter clan description"
                                                            required/>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label>Categories (select multiple)</Label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {categories?.map((category) => (
                                                                <div key={category.id}
                                                                     className="flex items-center space-x-2">
                                                                    <Checkbox
                                                                        id={`category-${category.id}`}
                                                                        checked={selectedCategories.includes(category)}
                                                                        onCheckedChange={() => handleCategoryToggle(category)}
                                                                    />
                                                                    <label
                                                                        htmlFor={`category-${category.id}`}
                                                                        className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                                    >
                                                                        {category.name}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        {/*<div className="flex items-center space-x-2 mb-2">
                            <Checkbox
                              id="unlimited"
                              checked={unlimited}
                              onCheckedChange={(checked) => setUnlimited(checked as boolean)}
                            />
                            <label
                              htmlFor="unlimited"
                              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Unlimited members
                            </label>
                          </div>*/}

                                                        {!unlimited && (
                                                            <div className="space-y-2">
                                                                <Label htmlFor="maxMembers">Maximum Members</Label>
                                                                <Input
                                                                    id="maxMembers"
                                                                    type="number"
                                                                    value={30}
                                                                    onChange={(e) => setMaxMembers(e.target.value)}
                                                                    placeholder="Enter max number of members"
                                                                    disabled={true}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 gap-4">
                                                        <div className="space-y-2">
                                                            <Label htmlFor="minBP">Minimum bp Required</Label>
                                                            <Input
                                                                id="minBP"
                                                                type="number"
                                                                value={minBP}
                                                                onChange={(e) => setMinBP(e.target.value)}
                                                                placeholder="Min BP to join"
                                                            />
                                                        </div>

                                                        {/*<div className="space-y-2">
                                                            <Label htmlFor="privacy">Privacy</Label>
                                                            <Select value={privacy} onValueChange={setPrivacy}>
                                                                <SelectTrigger id="privacy">
                                                                    <SelectValue placeholder="Select privacy"/>
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="public">Public</SelectItem>
                                                                    <SelectItem value="private">Private</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>*/}
                                                    </div>

                                                    <DialogFooter>
                                                        <Button type="submit">Create Clan</Button>
                                                    </DialogFooter>
                                                </form>
                                            </DialogContent>
                                        </Dialog>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div
                                    className="flex overflow-x-auto pb-4 sm:grid-cols-1 md:grid md:grid-cols-3 lg:grid-cols-6 gap-2 scrollbar-none">

                                    {isLeaguesLoading ? [...Array(6)].map((_, index) => ((
                                       <LeagueCardSkeleton />

                                    ))) : leagues?.sort((a, b) => (b.level) - (a.level)).map((league) => {
                                        const Icon = LucideIcons[league.icon as keyof typeof LucideIcons];
                                        return (
                                            <Card
                                                key={league.id}
                                                className="cursor-pointer overflow-hidden transition-transform w-full border border-dashed min-w-[260px] md:min-w-0"
                                                style={{
                                                    backgroundColor: league.color,
                                                    color: league.textColor,
                                                    borderColor: league.textColor
                                                }}
                                                onClick={() => {
                                                    navigate("/leagues")
                                                }}
                                            >
                                                <CardContent className="p-4 dark:bg-[#1F2937] dark:text-white h-full">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div
                                                            className="p-2 rounded-full"
                                                            style={{backgroundColor: `${league.textColor}20`}}
                                                        >

                                                            {isIconComponent(Icon) ? <Icon className="h-5 w-5"
                                                                                           style={{color: league.textColor}}/> : null}
                                                        </div>
                                                        <h3 className="font-bold">{league.name}</h3>
                                                    </div>
                                                    <div className="space-y-1 text-sm">
                                                        <p>
                                                            <strong>Criteria:</strong> {league.criteria.sort((a, b) => a.orderCol - b.orderCol)
                                                            .map(rule => rule.description)
                                                            .join(', ')}</p>
                                                        <p><strong>Benefits:</strong> {league.benefits}</p>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <h2 className="text-xl font-semibold">Active Clans</h2>

                                <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="relative w-full flex-2">
                                        <Search
                                            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4"/>
                                        <Input
                                            placeholder="Search clans..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-8 w-full"
                                        />
                                    </div>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="gap-2 dark:hover:text-black">
                                                <Filter className="h-4 w-4"/>
                                                Filter by Category
                                                {selectedCategoryFilters.length > 0 && (
                                                    <span
                                                        className="ml-1 text-xs bg-primary/20 rounded-full px-2 py-0.5">
                                                            {selectedCategoryFilters.length}
                                                    </span>
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-64 p-2">
                                            <div className="space-y-2">
                                                <h3 className="font-medium">Categories</h3>
                                                <div className="max-h-60 overflow-y-auto space-y-2">
                                                    {categories?.map((category) => (
                                                        <div key={category.id} className="flex items-center space-x-2">
                                                            <Checkbox
                                                                id={`filter-${category}`}
                                                                checked={selectedCategoryFilters.includes(category.id)}
                                                                onCheckedChange={() => handleCategoryFilterToggle(category.id)}
                                                            />
                                                            <label
                                                                htmlFor={`filter-${category}`}
                                                                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                            >
                                                                {category.name}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>

                                                {selectedCategoryFilters.length > 0 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="w-full mt-2"
                                                        onClick={() => setSelectedCategoryFilters([])}
                                                    >
                                                        Clear All
                                                    </Button>
                                                )}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {isClansLoading ? (
                                        [...Array(3)].map((_, i) => (<ClanCardSkeleton />))
                                    ) : filteredClans?.map((clan) => (
                                        <Card key={clan.id}
                                              className="flex flex-col h-full overflow-hidden border dark:bg-[#1F2937] dark:text-white">
                                            <CardHeader
                                                className="bg-[#E5F4EA] dark:bg-[#1F2937] p-4 flex flex-row items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="bg-[#155231] dark:bg-[#155231] text-white p-2 rounded-full">
                                                        <Users className="h-5 w-5"/>
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-[#155231] dark:text-white">{clan.name}</h3>
                                                        <div
                                                            className="flex items-center gap-1 text-sm text-[#155231]/80 dark:text-white/80">
                                                            <span>{clan.categoryNames.join(', ')}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div
                                                        className="text-xl font-bold text-[#155231] dark:text-white">{formatNumber(clan.totalPollPoints)}</div>
                                                    <div className="text-sm text-[#155231]/80 dark:text-white/80">Total
                                                        bp
                                                    </div>
                                                </div>

                                            </CardHeader>
                                            <CardContent className="flex-1 p-0 flex flex-col justify-between">
                                                <div className="max-h-[300px] overflow-x-auto dark:bg-[#1F2937]">
                                                    <Table>
                                                        <TableHeader className="bg-gray-50 dark:bg-gray-800">
                                                            <TableRow>
                                                                <TableHead>User</TableHead>
                                                                <TableHead className="text-right">bp</TableHead>
                                                                <TableHead className="text-right">Correct</TableHead>
                                                                {/*<TableHead>Badges</TableHead>*/}
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {clan.topMembers?.map((user) => (
                                                                <TableRow key={user.userId}>
                                                                    <TableCell
                                                                        className="font-medium">@{user.username}</TableCell>
                                                                    <TableCell
                                                                        className="text-right">{formatNumber(user.points)}</TableCell>
                                                                    <TableCell
                                                                        className="text-right">{user.correctAnswers}</TableCell>
                                                                    {/*<TableCell>
                                                                        <div className="flex space-x-1">
                                                                            {user.badges.map((badge, idx) => (
                                                                                <span key={idx}>{badge}</span>
                                                                            ))}
                                                                        </div>
                                                                    </TableCell>*/}
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                                <div
                                                    className="p-3 border-t bg-gray-50 dark:bg-[#1F2937] flex justify-between items-center">
                                                    <span
                                                        className="text-sm text-gray-500 dark:text-white/80">{clan.memberCount} members</span>
                                                    <ClanDetailModal clanId={clan.id} clanDetails={clanDetail}
                                                                     open={clanDetailsOpen}
                                                                     setOpen={setClanDetailsOpen} onApply={onApplyClan}
                                                                     isApplied={isApplied} userSession={session}
                                                                     userClanApplication={userClanApplication}
                                                                     showClanDetails={showClanDetails}
                                                                     clanDetailLoading={clanDetailLoading}
                                                                     onLeave={onLeaveClan}
                                                                     canApply={canApply}
                                                    />

                                                    {/*<Button className={"dark:hover:text-black"} variant="outline"
                                                            size="sm" onClick={(e) => {
                                                        showClanDetails(clan)
                                                    }}>
                                                        <Construction className="h-4 w-4 mr-1"/>
                                                        Details
                                                    </Button>*/}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <Card
                            className="p-6 overflow-hidden bg-white dark:bg-[#1F2937] text-[#155231] dark:text-white border border-dashed">
                            <div className="flex flex-col items-center justify-center text-center">
                                <div>
                                    <h3 className="text-xl font-bold mb-2">blankpoll Telegram App is Live!</h3>
                                    <p className="mb-4">Join our community on Telegram and start predicting polls,
                                        earning bp, and competing with other members in real-time. Get instant
                                        notifications for new polls and results!</p>
                                    <Button
                                        variant="telegram"
                                        className="gap-2 px-8 py-3 text-base"
                                        onClick={() => window.open('https://t.me/blankpollBot', '_blank')}
                                    >
                                        Join Now
                                        <Send className="h-5 w-5"/>
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </main>

            <Footer/>
        </>
    );
};

export default Clans;
